import { useMemo, useState, type ReactElement } from 'react';
import { Box, Group, SegmentedControl, Stack, Text } from '@mantine/core';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocTrendResult } from '@/shared/analytics/AnalyticsTypes';
import { formatCompact, formatMetricValue, metricLabel } from '@/shared/analytics/MetricFormatting';

interface TrendVizProps {
    definition: TopicDefinition;
    trend: AdHocTrendResult;
}

/** The metrics the trend can plot; `avgModelFit` is the model-fit score over time. */
type TrendMetric = 'turns' | 'totalTokens' | 'allocatedCostUsd' | 'avgModelFit';
const TREND_OPTIONS: { value: TrendMetric; label: string }[] = [
    { value: 'turns', label: 'Prompts' },
    { value: 'totalTokens', label: 'Total tokens' },
    { value: 'allocatedCostUsd', label: 'Cost' },
    { value: 'avgModelFit', label: 'Model fit' },
];
const TREND_METRIC_VALUES: string[] = TREND_OPTIONS.map((option) => option.value);

/** First-half vs second-half delta of the series, as a fraction — null when there's too little data to call a direction. */
function trendSlope(values: number[]): number | null {
    if (values.length < 4) return null;
    const mid = Math.floor(values.length / 2);
    const mean = (slice: number[]): number => slice.reduce((sum, v) => sum + v, 0) / slice.length;
    const first = mean(values.slice(0, mid));
    const second = mean(values.slice(mid));
    if (first === 0) return null;
    return (second - first) / first;
}

/** The "Trending ↑ +8%" chip — semantic colour, neutral inside ±2%. */
function SlopeChip({ slope }: { slope: number }): ReactElement {
    const flat = Math.abs(slope) < 0.02;
    const Icon = flat ? Minus : slope > 0 ? TrendingUp : TrendingDown;
    const color = flat ? 'var(--bm-color-text-dimmed)' : slope > 0 ? 'var(--bm-color-success)' : 'var(--bm-color-danger)';
    const label = flat ? 'Flat' : `Trending ${slope > 0 ? '+' : ''}${Math.round(slope * 100)}%`;
    return (
        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Icon size={13} color={color} />
            <Text size="xs" fw={700} style={{ color }}>
                {label}
            </Text>
        </Group>
    );
}

/**
 * Time-series area chart of a topic's headline metric across successive buckets.
 * Display-only: a time window is not expressible as an evidence slice, so the
 * drill-in for trend topics stays at the gallery → topic level.
 */
export default function TrendViz({ definition, trend }: TrendVizProps): ReactElement {
    const initialMetric: TrendMetric = TREND_METRIC_VALUES.includes(definition.headlineMetric) ? (definition.headlineMetric as TrendMetric) : 'turns';
    const [metric, setMetric] = useState<TrendMetric>(initialMetric);
    const gradientId = `trend-gradient-${definition.id}`;

    const data = useMemo(() => trend.points.map((point) => ({ bucket: point.bucket, value: point.metrics[metric] })), [trend.points, metric]);
    const slope = useMemo(() => trendSlope(data.map((point) => point.value)), [data]);

    return (
        <Stack gap="sm">
            <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
                <SegmentedControl size="xs" value={metric} onChange={(next) => setMetric(next as TrendMetric)} w="fit-content" data={TREND_OPTIONS} />
                {slope !== null && <SlopeChip slope={slope} />}
            </Group>
            <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', background: 'var(--bm-color-card)', boxShadow: 'var(--bm-card-shadow)', padding: 'var(--mantine-spacing-lg)', width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="var(--bm-color-brand-accent)" stopOpacity={0.35} />
                                <stop offset="100%" stopColor="var(--bm-color-brand-accent)" stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--bm-color-border)" vertical={false} />
                        <XAxis dataKey="bucket" tick={{ fontSize: 11 }} stroke="var(--bm-color-text-dimmed)" minTickGap={24} />
                        <YAxis domain={metric === 'avgModelFit' ? [0, 100] : undefined} tickFormatter={(value: number) => formatCompact(value)} tick={{ fontSize: 11 }} stroke="var(--bm-color-text-dimmed)" width={56} />
                        <Tooltip
                            formatter={(value: number) => [formatMetricValue(metric, value), metricLabel(metric)]}
                            contentStyle={{ background: 'var(--bm-color-body)', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', fontSize: 12 }}
                        />
                        <Area type="monotone" dataKey="value" name={metricLabel(metric)} stroke="var(--bm-color-brand-accent)" strokeWidth={2} fill={`url(#${gradientId})`} dot={false} />
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        </Stack>
    );
}
